import React, { Fragment } from "react";
import { RouteName, RouteParams } from "../../lib/routes";
import TopBox from "../common/TopBox";
import { ApiResult } from "../../lib/CmsApi";
import { format } from "date-fns";
import DefaultFullImageIntro from "../common/FullImageIntro";
import DefaultRichText from "../common/RichText";
import styled, { css } from "styled-components";
import ImageWithCaption from "../common/ImageWithCaption";
import Content from "../common/Content";
import DefaultLabel from "../common/Label";
import BookmarkButton from "../bookmarks/BookmarkButton";
import DefaultSimpleInformationCard from "../common/SimpleInformationCard";
import { colors, media, spacing, THEME_SCALE } from "../theme";
import RelatedNews from "../common/RelatedNews";
import Flex from "../common/Flex";
import { AriaDate } from "../../lib/formatters";
import FundingStoriesSection from "../sections/FundingStories";

const Label = styled(DefaultLabel)`
  ${spacing("margin-bottom", 32)}
`;

const RichText = styled(DefaultRichText)`
  h1,
  h2 {
    color: ${colors.darkgrey};
  }
`;
const StyledFlex = styled.div`
  display: flex;
  align-items: flex-start;
  > * {
    flex-basis: 50%;
    width: 50%;
  }
  > *:only-child {
    flex-basis: auto;
    width: 100%;
  }
  @media screen and ${media.mobile} {
    flex-direction: column;
    > * {
      flex-basis: auto;
      width: 100%;
    }
  }
`;
const ProgramCard = styled(DefaultSimpleInformationCard)`
  max-width: ${350 * THEME_SCALE}px;
`;
const ContentBlock = styled.div<{ indentLeft?: boolean; indentRight? }>`
  display: block;
  ${spacing("margin-bottom", 24)}
  ${p =>
    p.indentLeft &&
    css`
      padding-left: calc(3 / 12 * 100%);
    `}
  ${p =>
    p.indentRight &&
    css`
      padding-right: calc(3 / 12 * 100%);
    `}
    @media screen and ${media.mobile} {
      padding: 0 !important;
    }

`;
const StyledImageWithCaption = styled(ImageWithCaption)<{ swap?: boolean }>`
  margin-bottom: 1em;
  ${p =>
    p.swap
      ? css`
          padding-left: 4vw;
          order: 10;
        `
      : css`
          padding-right: 4vw;
        `}
  @media screen and ${media.mobile} {
    order:0;
    padding: 0;
  }
`;
const FloatContainer = styled.div``;
const CardsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  > a {
    margin-bottom: calc(4px * 4);
  }
  @media screen and ${media.tablet} {
    width: 100%;
    flex-direction: row;
    flex-wrap: wrap;
    > a {
      ${spacing("margin-bottom", 16)};
      &:not(:last-child) {
        ${spacing("margin-right", 16)}
      }
    }
  }
`;

const FullImageIntro = styled(DefaultFullImageIntro)`
  ${spacing("margin-bottom", 80)}
`;

const FloatRight = styled.div`
  float: right;
  margin: 0 0 2em 2em;

  @media screen and ${media.tablet} {
    float: none;
    margin: 0;
    width: 100%;
    ${spacing("margin-bottom", 80)}
  }
`;
const FullFlex = styled(Flex)`
  width: 100%;
  align-items: flex-end;
  @media screen and ${media.tablet} {
    align-items: flex-start;
  }
`;

export interface Props {
  content:
    | ApiResult<"loadMagazinPortraitsBySlug">
    | ApiResult<"loadMagazinNewsBySlug">;
  breadCrumpPath: { route: RouteName; params?: RouteParams; title: string }[];
  relatedNews?: ApiResult<"listMagazinNews">;
  relatedPortraits?: ApiResult<"listMagazinPortraits">;
  showFirstCategory?: boolean;
}
export default function MagazineDetailPage({
  content,
  breadCrumpPath,
  relatedNews,
  relatedPortraits,
  showFirstCategory
}: Props) {
  const withAside = "programs" in content && content.programs.length > 0;
  let offset = 0;
  let images = 0;
  return (
    <Fragment>
      <TopBox
        breadCrumpPath={
          [
            {
              route: "magazin",
              title: "Magazin"
            },
            ...breadCrumpPath
          ] as { title: string; route: RouteName; params: RouteParams }[]
        }
        title={content.title}
        overline={
          <>
            <AriaDate date={content.date} />
            {showFirstCategory &&
              content.categories[0] &&
              ` | ${content.categories[0].name}`}
          </>
        }
        infoBox={
          <FullFlex flexDirection={"column"}>
            <BookmarkButton
              bookmark={{
                id: content._id,
                title: content.title,
                href: ("browser" in process && window.location.pathname) || ""
              }}
              activeText={"Gemerkt"}
            >
              Artikel merken
            </BookmarkButton>
          </FullFlex>
        }
      />
      {content.headerImage && (
        <FullImageIntro
          image={content.headerImage}
          fallBackAlt={content.title}
        />
      )}
      <Content padded as="main" block>
        <FloatContainer>
          {"programs" in content && withAside && (
            <FloatRight>
              <Label tiny>Vermittelte Förderprogramme</Label>
              <CardsWrapper>
                {content.programs.map(prog => {
                  return (
                    <ProgramCard
                      key={prog.slug}
                      route="shortProgram"
                      params={{
                        slug: prog.slug
                      }}
                      label={(prog.type as any).name}
                      text={prog.name}
                    />
                  );
                })}
              </CardsWrapper>
            </FloatRight>
          )}
          {"description" in content && content.description && (
            <ContentBlock indentRight>
              <StyledFlex>
                <RichText
                  html={`<p><strong>${content.description}</strong></p>`}
                />
              </StyledFlex>
            </ContentBlock>
          )}
          {content.content &&
            content.content.map((c, idx) => {
              const hasImage = c._type === "image";
              if (hasImage) offset = idx % 2 ? 0 : 1;
              if (hasImage) images++;
              return (
                <ContentBlock
                  key={idx}
                  indentLeft={!hasImage && (offset + idx) % 2 !== 0}
                  indentRight={!hasImage && (offset + idx) % 2 === 0}
                >
                  <StyledFlex>
                    {c._type === "image" && (
                      <StyledImageWithCaption
                        image={c.image}
                        caption={c.imageSubTitle}
                        swap={images % 2 === 0}
                      />
                    )}
                    {c.text && <RichText html={c.text} />}
                  </StyledFlex>
                </ContentBlock>
              );
            })}
        </FloatContainer>
      </Content>
      {relatedNews && relatedNews.items && relatedNews.items.length > 0 && (
        <RelatedNews news={relatedNews} />
      )}
      {relatedPortraits &&
        relatedPortraits.items &&
        relatedPortraits.items.length > 0 && (
          <FundingStoriesSection
            headlineLabel={"Das könnte Sie auch interessieren"}
            items={relatedPortraits.items}
            sectionProps={{ reducedSectionPadding: true }}
          />
        )}
    </Fragment>
  );
}

MagazineDetailPage.getImages = ({ content }: Partial<Props>) => {
  let images = [];
  if (!content) return images;
  images.push(content.headerImage);
  if (content.content) {
    images = images.concat(
      content.content.map(c => c._type === "image" && c.image).filter(Boolean)
    );
  }

  return images;
};
